import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Plus, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Einkaufsliste — schlanke Variante:
 *  - Nur Artikelname + Plus-Button
 *  - Autocomplete-Vorschläge aus `shopping_history`
 *  - Flache Liste, neueste Einträge oben, Klick auf Häkchen entfernt
 */
export default function ShoppingList() {
  const [items, setItems]         = useState([]);
  const [text, setText]           = useState("");
  const [suggestions, setSug]     = useState([]);
  const [showSug, setShowSug]     = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading]     = useState(true);
  const inputRef                  = useRef(null);
  const boxRef                    = useRef(null);

  const load = () =>
    api.get("/shopping/items").then(r => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 1) { setSug([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/shopping/suggest", { params: { q, limit: 8 } });
        setSug(data.suggestions || []);
        setHighlight(-1);
      } catch { /* schweigend */ }
    }, 120);
    return () => clearTimeout(t);
  }, [text]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = async (rawText) => {
    const t = (rawText ?? text).trim();
    if (!t) return;
    try {
      const { data } = await api.post("/shopping/items", { text: t });
      setItems(prev => [data, ...prev]);
      setText("");
      setSug([]);
      setShowSug(false);
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Artikel nicht speichern");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/shopping/items/${id}`);
      setItems(prev => prev.filter(x => x.id !== id));
    } catch { toast.error("Konnte nicht entfernen"); }
  };

  const onKey = (e) => {
    if (showSug && suggestions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setHighlight(h => Math.max(h - 1, -1)); return; }
      if (e.key === "Enter" && highlight >= 0) {
        e.preventDefault();
        add(suggestions[highlight].text ?? suggestions[highlight]);
        return;
      }
      if (e.key === "Escape") { setShowSug(false); return; }
    }
    if (e.key === "Enter") { e.preventDefault(); add(); }
  };

  const openCount = items.length;

  return (
    <section className="msb-card" data-testid="shopping-list">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-amber-500 flex items-center justify-center">
          <ShoppingCart size={20} weight="fill" color="white"/>
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Einkaufsliste</h2>
          <p className="text-xs text-muted-foreground">
            {openCount === 0
              ? "Alles im Haus — nichts offen"
              : `${openCount} ${openCount === 1 ? "Artikel" : "Artikel"} offen`}
          </p>
        </div>
      </div>

      {/* Eingabe + Vorschläge */}
      <div className="relative" ref={boxRef}>
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => { setText(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onKeyDown={onKey}
            placeholder="z. B. Klopapier, Kaffee, Spülmittel …"
            className="rounded-xl flex-1"
            data-testid="shopping-input"
            autoComplete="off"/>
          <Button onClick={() => add()} className="rounded-xl bg-primary" data-testid="shopping-add-btn">
            <Plus size={16} weight="bold"/>
          </Button>
        </div>
        {showSug && suggestions.length > 0 && (
          <ul className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-xl border border-border bg-white shadow-lg py-1"
              data-testid="shopping-suggest">
            {suggestions.map((s, i) => {
              const label = s.text ?? s;
              return (
                <li key={`${label}-${i}`}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); add(label); }}
                    onMouseEnter={() => setHighlight(i)}
                    className={`w-full text-left px-3 py-1.5 text-sm transition ${i === highlight ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"}`}
                    data-testid={`shopping-suggest-${i}`}>
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Flache Liste */}
      <ul className="mt-4 space-y-1">
        {loading ? (
          <li className="text-sm text-muted-foreground italic">Lade …</li>
        ) : items.length === 0 ? (
          <li className="text-sm text-muted-foreground italic">Noch nichts auf der Liste. Tippe etwas oben ein.</li>
        ) : items.map(it => (
          <li key={it.id}
              className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/50 transition"
              data-testid={`shopping-item-${it.id}`}>
            <button
              type="button"
              onClick={() => remove(it.id)}
              title="Als erledigt markieren"
              className="w-6 h-6 rounded-md border-2 border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-white transition flex items-center justify-center flex-shrink-0"
              data-testid={`shopping-check-${it.id}`}>
              <Check size={14} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity"/>
            </button>
            <span className="flex-1 text-sm">{it.text}</span>
            <button
              type="button"
              onClick={() => remove(it.id)}
              title="Entfernen"
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600 transition p-1"
              aria-label="Entfernen">
              <X size={13} weight="bold"/>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
